import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Row,
  Spinner,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faSearch,
  faShieldAlt,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTable from "../../components/CustomTable";
import AuditStatusBadge from "../../components/audit/AuditStatusBadge";
import {
  isAuditorGeneralAccount,
  isReleaseAuditorAccount,
} from "../../components/audit/auditRole";
import { Routes } from "../../routes";

const CARD_LABELS = {
  1: { pending: "Awaiting AG review", flagged: "Flagged by AG", approved: "Audit approved" },
  2: { pending: "Awaiting release audit", flagged: "Flagged by audit", approved: "Audit approved" },
};

/**
 * Shared shell for the audit queues.
 *
 * Each stage has its own queue and its own counters: stage 1 lists releases at
 * Budget Approval Released, stage 2 lists the ones the AG has already approved.
 * Release rows open the review screen, where the Approve / Flag actions live.
 * The PV queue is still read-only until the PV audit screens land.
 */
const AuditQueue = ({ type = "releases", stage = 1, title, subtitle }) => {
  const isPv = type === "pvs";
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const cards = CARD_LABELS[stage] || CARD_LABELS[1];

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ statuses: {}, stage: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [approvalType, setApprovalType] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadSummary = useCallback(() => {
    fetchApi("audit/summary")
      .then((response) => {
        if (response?.success) {
          setSummary({
            statuses: isPv ? response.pvs || {} : response.releases || {},
            stage: (isPv ? null : response[stage === 2 ? "stage2" : "stage1"]) || {},
          });
        }
      })
      .catch(() => {
        /* the cards are informational; the queue error is the one that matters */
      });
  }, [isPv, stage]);

  const loadQueue = useCallback(() => {
    setLoading(true);
    setError("");

    const params = new URLSearchParams({
      type,
      stage: String(stage),
      page: String(page),
      pageSize: String(pageSize),
    });
    if (appliedSearch) params.set("search", appliedSearch);
    if (!isPv && approvalType) params.set("approval_type", approvalType);

    fetchApi(`audit/queue?${params.toString()}`)
      .then((response) => {
        if (response?.success) {
          setRows(response.data || []);
          setTotalRecords(response.totalRecords || 0);
          setTotalPages(response.totalPages || 0);
        } else {
          setRows([]);
          setError(response?.message || "Could not load the audit queue.");
        }
      })
      .catch(() => {
        setRows([]);
        setError("Could not load the audit queue. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [type, stage, page, pageSize, appliedSearch, approvalType, isPv]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const statuses = summary.statuses || {};
  const stageCounts = summary.stage || {};

  // The stage block is the honest count for this dashboard's queue; the status
  // map is only a fallback for an older backend that does not send it yet.
  const pending =
    stageCounts.queue ??
    (stage === 2
      ? statuses["AG Approved"] || 0
      : statuses["Pending AG Review"] || statuses["Not Audited"] || 0);
  const approved = stageCounts.approved ?? (statuses["Audit Approved"] || 0);
  const flagged =
    stageCounts.flagged ??
    ((stage === 2 ? 0 : statuses["AG Flagged"] || 0) + (statuses["Audit Flagged"] || 0));

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const fields = isPv
    ? [
        { title: "PV Code", value: "pv_code" },
        { title: "MDA", value: "mda_name" },
        {
          title: "Amount",
          custom: true,
          component: (item) => <div className="text-end">{formatNumber(item.amount)}</div>,
        },
        { title: "Date", value: "date" },
        { title: "PV Status", value: "status" },
        {
          title: "Audit Status",
          custom: true,
          component: (item) => <AuditStatusBadge status={item.audit_status} />,
        },
      ]
    : [
        { title: "Approval No", value: "approval_no" },
        { title: "MDA", value: "mda_name" },
        { title: "Type", value: "approval_type" },
        {
          title: "Amount",
          custom: true,
          component: (item) => <div className="text-end">{formatNumber(item.amount)}</div>,
        },
        { title: "Collection Date", value: "collection_date" },
        {
          title: "Audit Status",
          custom: true,
          component: (item) => <AuditStatusBadge status={item.audit_status} />,
        },
        {
          title: "Exceptions",
          className: "text-center",
          custom: true,
          component: (item) =>
            Number(item.exception_count) > 0 ? (
              <Badge bg="danger">{item.exception_count}</Badge>
            ) : (
              <span className="text-muted">—</span>
            ),
        },
        {
          title: "Action",
          className: "text-center",
          custom: true,
          component: (item) => (
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                navigate(
                  `${Routes.audit.releaseView}?approval_no=${encodeURIComponent(
                    item.approval_no
                  )}&stage=${stage}`
                )
              }
            >
              Review
            </Button>
          ),
        },
      ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-3 mb-md-0">
          <h4 className="mb-1">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
            {title}
          </h4>
          <p className="text-muted mb-0">{subtitle}</p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={() => { loadQueue(); loadSummary(); }} disabled={loading}>
          <FontAwesomeIcon icon={faSync} className={`me-1 ${loading ? "fa-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Row className="mb-4">
        <Col xs={4}>
          <Card className="text-center h-100">
            <Card.Body className="p-3">
              <h3 className="text-warning mb-1">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                {pending}
              </h3>
              <small className="text-muted">{isPv ? "Awaiting audit" : cards.pending}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={4}>
          <Card className="text-center h-100">
            <Card.Body className="p-3">
              <h3 className="text-danger mb-1">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                {flagged}
              </h3>
              <small className="text-muted">{isPv ? "Flagged" : cards.flagged}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={4}>
          <Card className="text-center h-100">
            <Card.Body className="p-3">
              <h3 className="text-success mb-1">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                {approved}
              </h3>
              <small className="text-muted">{cards.approved}</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {stage === 2 && isAuditorGeneralAccount(user) && (
        <div className="alert alert-info py-2">
          <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
          This queue belongs to the release auditors. You can read it, but the second
          review and its Audit Approved / Audit Flagged outcome are recorded by a
          release auditor — not by the Auditor General.
        </div>
      )}

      {isReleaseAuditorAccount(user) &&
        !String(user?.auditor_mdas || "").trim() &&
        String(user?.auditor_scope || "").toLowerCase() !== "capital" && (
          <div className="alert alert-warning py-2">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
            No MDAs are assigned to your auditor profile, so this queue is empty.
            Ask an administrator to assign your MDAs in User Management.
          </div>
        )}

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={submitSearch}>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={isPv ? 9 : 6}>
                <Form.Group>
                  <Form.Label className="small text-muted">Search</Form.Label>
                  <div className="filter-search">
                    <FontAwesomeIcon icon={faSearch} className="filter-search-icon" />
                    <Form.Control
                      type="text"
                      placeholder={isPv ? "Search PV code or MDA..." : "Search approval no or MDA..."}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
              {!isPv && (
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="small text-muted">Release type</Form.Label>
                    <Form.Select
                      value={approvalType}
                      onChange={(event) => {
                        setApprovalType(event.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">All types</option>
                      <option value="Capital">Capital</option>
                      <option value="Recurrent">Recurrent</option>
                      <option value="Overhead">Overhead</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col xs={12} md={3}>
                <Button type="submit" variant="primary" className="w-100">
                  Apply
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card border="light" className="table-wrapper shadow-sm">
        <Card.Body className="p-0">
          {error ? (
            <div className="alert alert-danger m-3 mb-0">{error}</div>
          ) : loading && !rows.length ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 mb-0 text-muted">Loading audit queue…</p>
            </div>
          ) : (
            <CustomTable fields={fields} data={rows} />
          )}
        </Card.Body>

        {totalPages > 1 && (
          <Card.Footer className="px-3 border-0 d-flex align-items-center justify-content-between">
            <small className="fw-bold">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalRecords)} of {totalRecords}
            </small>
            <Pagination className="mb-0">
              <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(p - 1, 1))} />
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>
    </>
  );
};

export default AuditQueue;
